import { useMemo } from "react";

import { useOrderMarkets } from "@web-app/api";
import { Order, OrderDeliveryType, State } from "@web-app/types";
import { Route } from "@web-app/types";

export default function useStatesView({
  countryCode = "US",
  unroutedOrders,
  routedOrdersMap,
  routes,
  enabled = true,
}: {
  countryCode: string;
  enabled?: boolean;
  routedOrdersMap: Map<string, Order>;
  routes: Route[];
  unroutedOrders: Order[];
}) {
  // Only fetch states when enabled and we have orders to process
  const hasOrders = unroutedOrders.length > 0 || routes.length > 0;
  const shouldFetch = enabled && hasOrders;

  const { data: states, isFetching } = useOrderMarkets({
    countryCode,
    enabled: shouldFetch,
  });

  const groupedOrderStates = useMemo(() => {
    const orderStates = new Map<
      string,
      {
        order?: Order[];
        route?: Route[];
      }
    >();

    const addOrder = (stateId: string, order: Order) => {
      const groupedState = orderStates.get(stateId);
      orderStates.set(stateId, {
        ...groupedState,
        order: [...(groupedState?.order || []), order],
      });
    };

    const addRoute = (stateId: string, route: Route) => {
      const groupedState = orderStates.get(stateId);
      orderStates.set(stateId, {
        ...groupedState,
        route: [...(groupedState?.route || []), route],
      });
    };

    for (const order of unroutedOrders) {
      if (order.type === OrderDeliveryType.ROUTE_OPT_PICKUP) {
        addOrder(order.pickup.stateId, order);
      }

      if (order.type === OrderDeliveryType.ROUTE_OPT_DROPOFF) {
        addOrder(order.dropoff.stateId, order);
      }

      if (order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF) {
        addOrder(order.pickup.stateId, order);
      }
    }

    for (const route of routes) {
      const firstOrder = route.ordersOrder
        .map((routeOrder) => routedOrdersMap.get(routeOrder.id))
        .find((order): order is Order => Boolean(order));

      if (!firstOrder) {
        continue;
      }

      if (firstOrder.type === OrderDeliveryType.ROUTE_OPT_PICKUP) {
        addRoute(firstOrder.pickup.stateId, route);
      }

      if (firstOrder.type === OrderDeliveryType.ROUTE_OPT_DROPOFF) {
        addRoute(firstOrder.dropoff.stateId, route);
      }
    }

    return orderStates;
  }, [unroutedOrders, routedOrdersMap, routes]);

  return {
    groupedOrderStates,
    isLoading: isFetching,
    states: states || new Map<string, State>(),
  };
}
